import React, { useState } from 'react';

import { Checkbox, Col, Row, Space, Typography } from 'antd';

import classnames from 'classnames';

import {
  dialogFunction,
  FormButton,
  FormSaveButton,
  WithLoading,
} from 'common/components';

import * as CODPayment from './CODPayment';

import { AlertEmptyCard } from 'pages/company-profile/components/tabs/billing/MemberCardInformation';

import { useVisibleModal } from 'hooks';
import {
  useGetSavedMemberCard,
  useListenerMessageSaveCard,
  useMemberPaymentActions,
} from 'pages/company-profile/components/tabs/billing/hooks';

import { useCreditCardEula } from 'hooks/useCreditCardEula';
import {
  usePayNowSavedMemberCard,
  useSubmittingCODPayment,
  useValidatingEulaCard,
} from './hooks';
import { useBrokerHasInitiateBillingEvent } from 'hooks/impersonate/useBrokerHasInitiateBillingEvent';

import { checkStatusCODCharge, transformChargeItemsForCOD } from './utils';

import { COD_PAYMENT_DESCRIPTION } from './constants';

import './PaymentChargeValidating.less';

const hasPermissionPayWithMemberCC = true;
const hasPermissionMemberBillingProperties = true;
const isBlockingCharge = false;

export const CODChargeValidating = (props) => {
  const {
    fee,
    typeView,
    children,
    confirmRequest = () => {},
    chargeItems = [],
    onCancel,
  } = props;

  const brokerHasInitiateBillingEvent = useBrokerHasInitiateBillingEvent();

  const [visibleCODModal, { onModal: onModalCOD, offModal: offModalCOD }] =
    useVisibleModal();

  const [visibleAddCC, { onModal: onModalAddCC, offModal: offModalAddCC }] =
    useVisibleModal();

  const [hasAcceptedCCTerm, setHasAcceptedCCTerm] = useState(false);

  const { hasCreditCard, savedMemberCard, fetchSavedMemberCard } =
    useGetSavedMemberCard();

  const { showCardEulaModal } = useCreditCardEula({
    context: 'billing',
  });

  const { iframeUrl, loadingIframe, handleSaveCard } = useMemberPaymentActions({
    memberPaymentProfileId: savedMemberCard?.memberPaymentProfileId,
    fetchSavedMemberCard,
    offModal: offModalAddCC,
  });

  useListenerMessageSaveCard({
    offModal: offModalAddCC,
    fetchSavedMemberCard,
    modeAction: 'add',
  });

  useValidatingEulaCard({
    dependencies: [savedMemberCard?.memberPaymentProfileId],
    handleAddCreditCard: () => {
      onModalAddCC();
      handleSaveCard({ modeAction: 'add' });
    },
  });

  const codPayNow = usePayNowSavedMemberCard({ onCancel });

  const isSubmittingCOD = useSubmittingCODPayment();

  const statusCODCharge = checkStatusCODCharge({
    hasCreditCard,
    hasPermissionPayWithMemberCC,
    hasPermissionMemberBillingProperties,
  });

  const handleCODCharge = () => onModalCOD();

  const handleCancelCODModal = () => {
    offModalCOD();
    setHasAcceptedCCTerm(false);
  };

  const handleCODPayNow = async () => {
    const isSendToCapture = typeView?.toLowerCase() === 'sendixonecapture';

    let codParams = {
      totalAmount: fee,
      isSendToCapture,
      chargeItems: transformChargeItemsForCOD(chargeItems),
      chargeDescription: COD_PAYMENT_DESCRIPTION[typeView],
    };

    await codPayNow.mutateAsync(codParams, {
      onSuccess: (response) => {
        if (response?.isSuccess) {
          confirmRequest();
        } else {
          offModalCOD();
        }
      },
      onError: (error) => {
        const errorMsg = error?.message ?? 'Failed to charge';
        onCancel?.();
        dialogFunction({
          type: 'warn',
          content: errorMsg,
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
        });
      },
    });
  };

  return (
    <>
      {typeof children === 'function'
        ? children({ handleCODCharge })
        : children}

      {visibleCODModal && (
        <CODPayment.ModalConfirm
          visible={visibleCODModal}
          onCancel={handleCancelCODModal}
          okButtonProps={{
            disabled: !hasCreditCard,
            style: {
              display: isBlockingCharge ? 'none' : 'inline',
            },
            loading: codPayNow.isLoading,
          }}
          hideWarningIcon={isBlockingCharge}
          wrapClassName={classnames({
            'cod__modal-confirm--empty-card': !hasCreditCard,
          })}
          footer={
            <>
              {hasCreditCard ? (
                <Row justify='space-between' align='middle'>
                  <Col>
                    <Space size={2}>
                      <Checkbox
                        disabled={isSubmittingCOD}
                        checked={hasAcceptedCCTerm}
                        onChange={(event) =>
                          setHasAcceptedCCTerm(event.target.checked)
                        }
                      />
                      <CODPayment.ContentConfirmRequest />
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <FormButton
                        type='secondary'
                        text='Cancel'
                        icon={null}
                        onClick={handleCancelCODModal}
                      />
                      <FormSaveButton
                        text='Submit'
                        disabled={
                          !hasAcceptedCCTerm || !brokerHasInitiateBillingEvent
                        }
                        onClick={handleCODPayNow}
                        loading={isSubmittingCOD}
                      />
                    </Space>
                  </Col>
                </Row>
              ) : (
                <AlertEmptyCard />
              )}
            </>
          }
        >
          {isBlockingCharge ? (
            <CODPayment.BlockChargeSection />
          ) : (
            <>
              {statusCODCharge === 'readyToCharge' ? (
                <CODPayment.ReadyToChargeSection fee={fee} typeView={typeView}>
                  <CODPayment.CreditCardSection>
                    <CODPayment.CreditCardInfo memberCard={savedMemberCard} />
                  </CODPayment.CreditCardSection>
                </CODPayment.ReadyToChargeSection>
              ) : (
                <CODPayment.MissingCardSection>
                  <CODPayment.CreditCardSection>
                    <CODPayment.AddBank
                      onClick={() => {
                        showCardEulaModal({ when: 'add-card' });
                      }}
                    />
                  </CODPayment.CreditCardSection>

                  {visibleAddCC && (
                    <CODPayment.CreditCardIframeModal
                      title={
                        <Typography.Paragraph>
                          <Typography.Text style={{ fontWeight: '700' }}>
                            Add Payment Information
                          </Typography.Text>{' '}
                          <Typography.Text style={{ fontWeight: '400' }}>
                            - This card will be used for annual membership dues,
                            product captures and any authorized ancillary
                            charges.
                          </Typography.Text>
                        </Typography.Paragraph>
                      }
                      visible={visibleAddCC}
                      width={1200}
                      onCancel={offModalAddCC}
                    >
                      <WithLoading loading={loadingIframe}>
                        <CODPayment.CreditCardIframe
                          iframeUrl={iframeUrl}
                          width={1200}
                        />
                      </WithLoading>
                    </CODPayment.CreditCardIframeModal>
                  )}
                </CODPayment.MissingCardSection>
              )}
            </>
          )}
        </CODPayment.ModalConfirm>
      )}
    </>
  );
};
